import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { summarizeCoverage, type AccountCoverage } from '../../lib/publishing/multiAccount';
import type { FanOutResponse } from './api';

// Publishing multi-account — confirm + results (2026-07-28). Two phases in one
// modal: (1) confirm the fan-out (how many accounts will receive it, how many get
// nothing), then (2) the per-account results (the resilience made visible —
// created / draft-saved-on-error / skipped / failed). Modelled on the broadcast
// ConfirmSendModal → results flow.

const MODE_VERB: Record<'draft' | 'schedule' | 'now', string> = {
  draft: 'Save',
  schedule: 'Schedule',
  now: 'Publish',
};

export function ConfirmFanOutModal({
  plan,
  publishMode,
  busy,
  result,
  errorText,
  onConfirm,
  onClose,
}: {
  plan: AccountCoverage[];
  publishMode: 'draft' | 'schedule' | 'now';
  busy: boolean;
  // Set once the fan-out returns → switches the modal to the results view.
  result: FanOutResponse | null;
  errorText: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const summary = summarizeCoverage(plan);
  const zeroCoverage = plan.filter((p) => !p.hasCoverage);

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        {result ? (
          <ResultsView result={result} onClose={onClose} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {MODE_VERB[publishMode]} to {summary.willReceive} account
                {summary.willReceive === 1 ? '' : 's'}?
              </DialogTitle>
              <DialogDescription>
                One post is created per account, with the caption resolved to each account&rsquo;s
                own details, posting only to the destinations that account has connected.
              </DialogDescription>
            </DialogHeader>

            {zeroCoverage.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <p className="font-medium">
                  {zeroCoverage.length} selected account{zeroCoverage.length === 1 ? '' : 's'} will
                  receive nothing (no connected destination) and won&rsquo;t be created:
                </p>
                <p className="mt-1 text-xs">
                  {zeroCoverage.map((p) => p.account.name).join(', ')}
                </p>
              </div>
            )}

            {errorText && <p className="text-sm text-destructive">{errorText}</p>}

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={onConfirm} disabled={busy || summary.willReceive === 0}>
                {busy ? 'Working…' : `${MODE_VERB[publishMode]} ${summary.willReceive}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResultsView({ result, onClose }: { result: FanOutResponse; onClose: () => void }) {
  const created = result.results.filter((r) => r.status === 'created');
  const draftSaved = created.filter((r) => r.dispatchError);
  const skipped = result.results.filter((r) => r.status === 'skipped');
  const failed = result.results.filter((r) => r.status === 'failed');

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {result.created} posted · {result.skipped} skipped · {result.failed} failed
        </DialogTitle>
        <DialogDescription>
          Each created post is on the calendar and can be edited or deleted individually.
        </DialogDescription>
      </DialogHeader>

      <div className="max-h-72 space-y-3 overflow-y-auto text-sm">
        {draftSaved.length > 0 && (
          <div>
            <p className="font-medium text-amber-700">Saved as drafts (publishing failed):</p>
            <ul className="mt-1 space-y-0.5">
              {draftSaved.map((r) => (
                <li key={r.accountId} className="text-xs text-slate-600">
                  {r.accountName ?? r.accountId} — {r.dispatchError}
                </li>
              ))}
            </ul>
          </div>
        )}
        {failed.length > 0 && (
          <div>
            <p className="font-medium text-destructive">Failed:</p>
            <ul className="mt-1 space-y-0.5">
              {failed.map((r) => (
                <li key={r.accountId} className="text-xs text-slate-600">
                  {r.accountName ?? r.accountId} — {r.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
        {skipped.length > 0 && (
          <div>
            <p className="font-medium text-slate-500">Skipped (no connected destination):</p>
            <p className="mt-1 text-xs text-slate-500">
              {skipped.map((r) => r.accountName ?? r.accountId).join(', ')}
            </p>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button onClick={onClose}>Done</Button>
      </DialogFooter>
    </>
  );
}
